import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, useInView, animate } from 'framer-motion';
import { TrendingUp, Utensils, Beer, Truck, Store } from 'lucide-react';

// Evita que a captura estática do react-snap grave o contador em 0 ou o
// float em posição intermediária — mesma guarda usada em PainList/MobileExplain.
const isPrerendering = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const TARGET = 250;

const categories = [
  { icon: Utensils, label: 'Restaurantes' },
  { icon: Beer, label: 'Bares' },
  { icon: Truck, label: 'Delivery' },
  { icon: Store, label: 'Redes com várias unidades' },
];

const fadeUp = (delay = 0) => (isPrerendering ? {} : {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.5 },
  transition: { duration: 0.5, delay, ease: 'easeOut' },
});

const float = (delay = 0) => (isPrerendering ? {} : {
  animate: { y: [0, -14, 0] },
  transition: { duration: 5, repeat: Infinity, ease: 'easeInOut', delay },
});

const Section = styled.section`
  position: relative;
  width: 100%;
  padding: 8rem 2rem;
  overflow: hidden;
  background: linear-gradient(135deg, var(--color-brand) 0%, var(--color-brand) 45%, var(--color-brand-deep) 100%);

  @media screen and (max-width: 768px) {
    padding: 5rem 1.5rem;
  }
`;

const DotGrid = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.14;
  background-image: radial-gradient(rgba(255, 255, 255, 0.7) 1px, transparent 1px);
  background-size: 26px 26px;
  pointer-events: none;
`;

const Glow = styled.div`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
`;

const GlowTop = styled(Glow)`
  width: 520px;
  height: 520px;
  background: var(--color-accent-soft);
  opacity: 0.18;
  filter: blur(110px);
  top: -200px;
  right: -140px;
`;

const GlowBottom = styled(Glow)`
  width: 460px;
  height: 460px;
  background: var(--color-brand-light);
  opacity: 0.16;
  filter: blur(110px);
  bottom: -180px;
  left: -140px;
`;

const Watermark = styled(Utensils)`
  position: absolute;
  width: 480px;
  height: 480px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #ffffff;
  opacity: 0.035;
  pointer-events: none;

  @media screen and (max-width: 768px) {
    width: 320px;
    height: 320px;
  }
`;

const FloatingBadge = styled(motion.div)`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(6px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);

  @media screen and (max-width: 900px) {
    display: none;
  }
`;

const BadgeTopRight = styled(FloatingBadge)`
  top: 10%;
  right: 10%;
`;

const BadgeBottomLeft = styled(FloatingBadge)`
  bottom: 14%;
  left: 8%;
`;

const Content = styled.div`
  position: relative;
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
`;

const Eyebrow = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.15rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-family: var(--font-body);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-accent-soft);
  margin-bottom: 2.25rem;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Headline = styled(motion.p)`
  font-family: var(--font-body);
  font-size: clamp(1.05rem, 2vw, 1.3rem);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.5rem;
`;

const NumberRow = styled(motion.div)`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.35rem;
  margin-bottom: 1.5rem;
`;

const Number = styled.span`
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(4.5rem, 13vw, 8.5rem);
  line-height: 1;
  color: var(--color-white);
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
`;

const Plus = styled.span`
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(2.5rem, 6.5vw, 4.2rem);
  color: var(--color-accent-soft);
`;

const SubLabel = styled(motion.p)`
  font-family: var(--font-display);
  font-size: clamp(1.4rem, 2.8vw, 2.1rem);
  font-weight: 700;
  color: var(--color-white);
  margin-bottom: 3rem;
  line-height: 1.3;
`;

const ChipList = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`;

const CategoryChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.15rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  font-family: var(--font-body);
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  transition: background 0.25s ease, transform 0.25s ease;

  svg {
    width: 16px;
    height: 16px;
    color: var(--color-accent-soft);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.14);
    transform: translateY(-2px);
  }
`;

const Stats = () => {
  const numberRef = useRef(null);
  const isInView = useInView(numberRef, { once: true, amount: 0.6 });
  const [count, setCount] = useState(isPrerendering ? TARGET : 0);

  useEffect(() => {
    if (isPrerendering || !isInView) return undefined;

    const controls = animate(0, TARGET, {
      duration: 1.7,
      ease: 'easeOut',
      onUpdate: (value) => setCount(Math.round(value)),
    });

    return () => controls.stop();
  }, [isInView]);

  return (
    <Section id="numeros">
      <DotGrid />
      <GlowTop />
      <GlowBottom />
      <Watermark strokeWidth={1} aria-hidden="true" />

      <BadgeTopRight {...float(0)}>
        <TrendingUp size={22} color="#fff" />
      </BadgeTopRight>
      <BadgeBottomLeft {...float(1.6)}>
        <Utensils size={20} color="#fff" />
      </BadgeBottomLeft>

      <Content>
        <Eyebrow {...fadeUp(0)}>
          <TrendingUp />
          Referência em gestão para bares e restaurantes
        </Eyebrow>

        <Headline {...fadeUp(0.05)}>
          Enquanto uns ainda fecham o caixa na calculadora,
        </Headline>

        <NumberRow ref={numberRef} {...fadeUp(0.1)}>
          <Number>{count}</Number>
          <Plus>+</Plus>
        </NumberRow>

        <SubLabel {...fadeUp(0.15)}>
          negócios já rodam com o SofChef — todos os dias, em todo o Brasil
        </SubLabel>

        <ChipList {...fadeUp(0.2)}>
          {categories.map(({ icon: Icon, label }) => (
            <CategoryChip key={label}>
              <Icon strokeWidth={2} />
              {label}
            </CategoryChip>
          ))}
        </ChipList>
      </Content>
    </Section>
  );
};

export default Stats;
